import Cookies from "js-cookie";
import { useRouter } from "next/router";
import { useEffect, useMemo, useState } from "react";
import { useCustomerAnalytics } from "../../../services/hooks/useCustomerAnalytics";
import { useModalHook } from "../../../services/hooks/useModal";
import {
  useAppDispatch,
  useAppSelector,
} from "../../../services/hooks/useReduxHooks";
import { useTranslationI18 } from "../../../services/hooks/useTranslationI18";
import {
  setCurrentDeliveryConfig,
  setSelectedAddress,
} from "../../../services/redux/slices/deliverySlice";
import {
  setAddressSelectionDrawerState,
  setAnimatedGlobalBannerState,
} from "../../../services/redux/slices/globalComponentsToggleSlice";
import { setSelectedFranchise } from "../../../services/redux/slices/configSlice";
import { setQueryParam } from "../../../services/redux/slices/urlParamsSlice";
import {
  COOKIE_ENUM,
  CUSTOMER_ANALYTICS_EVENT,
  DeliveryTypeDisplayName,
  LocalStorageEnum,
  OrderTypesEnum,
  PagesEnum,
  URL_PARAMS,
} from "../../../utils/enums";
import {
  checkAddressAvailabliltyInFranchisesV2,
  findDeliveryConfigForBranch,
  sortAddress,
} from "../../../utils/help/delivery/delivery";
import { selectCartItemsNumber } from "../../../services/redux/selectors/cartSelectors";
import { getPageLink } from "../../../utils/help/navigation/navigation";
import { Address, AddressPlus, INVERTED_SECTIONS } from "../../../utils/types";
import { selectCurrentDeliveryConfig } from "../../../services/redux/selectors/deliverySelectors";

type OrderTypeTab = OrderTypesEnum | "car_pickup" | `delivery_type_${number}`;

export type UseDeliverySelectionDrawerProps = {
  nextPage?: PagesEnum;
};

export const useDeliverySelectionDrawer = ({
  nextPage,
}: UseDeliverySelectionDrawerProps) => {
  const { handleModal } = useModalHook();
  const { t } = useTranslationI18();
  const dispatch = useAppDispatch();
  const router = useRouter();
  const addCustomerAnalytics = useCustomerAnalytics();
  const { franchise } = router.query;

  // ─── Redux selectors ───────────────────────────────────────────────────────
  const orderTypeParam = useAppSelector(
    (state) => state.queryParams.orderTypeParam
  );
  const drawerState = useAppSelector(
    (state) => state.globalComponentsToggleSlice.isOpenAddressSelection
  );
  const delivery = useAppSelector((state) => state.delivery);
  const currentDeliveryConfig = useAppSelector(selectCurrentDeliveryConfig);
  const restaurantInfo = useAppSelector((state) => state.config.restaurantInfo);
  const hasWelcomeDrawerV3 = useAppSelector(
    (state) => state.config.restaurantConfig?.hasWelcomeDrawerV3
  );
  const franchises =
    useAppSelector((state) => state.config.restaurantInfo?.franchises) || [];

  // ─── Derived ───────────────────────────────────────────────────────────────
  const isDeliveryDrawerInverted =
    restaurantInfo?.colors?.invertedSections?.includes(
      INVERTED_SECTIONS.DELIVERY_SELECTION_DRAWER
    );

  // ─── Franchises & tabs ─────────────────────────────────────────────────────
  const activeFranchises = useAppSelector(
    (state) => state.config.activeFranchises
  );
  const selectedFranchiseRedux = useAppSelector(
    (state) => state.config.selectedFranchise
  );
  // selectCartItemsNumber, not cartLoyaltyV2Reducer.cart.cartItemsNumber: with
  // `useLoyaltyService` on, the count lives in the loyalty cart lines and that
  // raw field stays 0. Reading it directly made the "cart will be cleared"
  // guard below think the cart was empty, so switching branch skipped the
  // confirmation entirely on loyalty-service restaurants (prepits/project#3913).
  const cartItemsNumber = useAppSelector(selectCartItemsNumber);

  const uniqueDeliveryTypes = Object.values(
    activeFranchises
      .flatMap((f) => f.franchiseConfig?.deliveryV2 ?? [])
      .reduce<Record<number, { id: number; displayName: string }>>(
        (acc, config) => {
          const { id, displayName } = config.deliveryType;
          if (!acc[id]) {
            acc[id] = { id, displayName };
          }

          return acc;
        },
        {}
      )
  );
  const hasTakeOut = activeFranchises.some((f) => f.franchiseConfig?.takeOut);
  const hasDineIn = activeFranchises.some((f) => f.franchiseConfig?.dineIn);

  // Resolve a tab (car pickup / delivery_type_{id}) to its underlying delivery
  // type id — undefined for non-delivery tabs (takeout/dine-in/unset).
  const getDeliveryTypeIdForTab = (
    tab: OrderTypeTab | undefined
  ): number | undefined => {
    if (tab === "car_pickup") {
      return uniqueDeliveryTypes.find(
        (dt) => dt.displayName === DeliveryTypeDisplayName.CAR_PICKUP
      )?.id;
    }
    if (typeof tab === "string" && tab.startsWith("delivery_type_")) {
      return Number(tab.replace("delivery_type_", ""));
    }

    return undefined;
  };

  // Derive initial tab from current orderTypeParam — prefer currentDeliveryConfig
  // so the drawer reopens on the same delivery type the user already confirmed.
  const getInitialTab = (): OrderTypeTab | undefined => {
    if (!orderTypeParam) {
      return undefined;
    }
    if (orderTypeParam === OrderTypesEnum.DELIVERYV2) {
      const currentDeliveryType = currentDeliveryConfig?.deliveryType;
      if (currentDeliveryType) {
        return currentDeliveryType.displayName ===
          DeliveryTypeDisplayName.CAR_PICKUP
          ? "car_pickup"
          : (`delivery_type_${currentDeliveryType.id}` as OrderTypeTab);
      }
      if (uniqueDeliveryTypes.length > 0) {
        const first = uniqueDeliveryTypes[0];

        return first.displayName === DeliveryTypeDisplayName.CAR_PICKUP
          ? "car_pickup"
          : (`delivery_type_${first.id}` as OrderTypeTab);
      }

      return undefined;
    }

    return orderTypeParam as OrderTypeTab;
  };

  const [selectedTab, setSelectedTab] = useState<OrderTypeTab | undefined>(
    getInitialTab
  );
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedFranchiseId, setSelectedFranchiseId] = useState<
    number | undefined
  >(selectedFranchiseRedux?.id);

  // Fires the "changed order type" event on Apply, but only when the user
  // actually switched to a different tab than the one the drawer opened on
  // (getInitialTab) — per spec this must not fire when nothing was selected
  // before. Keyed by the page the drawer was opened from.
  const reportOrderTypeChangeIfNeeded = () => {
    if (!orderTypeParam || selectedTab === getInitialTab()) {
      return;
    }
    addCustomerAnalytics(
      router.pathname.includes(PagesEnum.Cart)
        ? CUSTOMER_ANALYTICS_EVENT.CHANGE_ORDER_TYPE_FROM_CART
        : CUSTOMER_ANALYTICS_EVENT.CHANGE_ORDER_TYPE_FROM_MENU
    );
  };

  // Applying a change lands the customer back where they opened the drawer.
  // Opening it from the cart and applying used to push Home unconditionally,
  // which dropped them out of checkout and made them navigate back to the cart
  // themselves. Only covers the apply-in-place paths — switching to a different
  // branch clears the cart, and that flow deliberately returns Home.
  // Resolved when applying rather than on render: `pathname` is absent from
  // partial router mocks, and evaluating it in the hook body would throw for
  // every consumer that never applies anything.
  // The caller's own nextPage wins when it sets one (TipScreenV2 passes Tip,
  // CartScreen passes Cart) — it knows where the customer should land better
  // than a pathname sniff. Falling through to Home from the tip screen dropped
  // customers out of checkout after changing branch/order type.
  const getDrawerReturnPage = () => {
    if (nextPage) {
      return nextPage;
    }
    if (router.pathname?.includes(PagesEnum.Cart)) {
      return PagesEnum.Cart;
    }
    if (router.pathname?.includes(PagesEnum.Tip)) {
      return PagesEnum.Tip;
    }

    return PagesEnum.Home;
  };

  // Branch-name search in the drawer — report once per search session (when the
  // field goes from empty to non-empty), not on every keystroke.
  const handleBranchSearchChange = (value: string) => {
    if (!searchQuery.trim() && value.trim()) {
      addCustomerAnalytics(CUSTOMER_ANALYTICS_EVENT.SEARCHED_FOR_BRANCH);
    }
    setSearchQuery(value);
  };

  // ─── Local state ───────────────────────────────────────────────────────────
  const [addresses, setAddresses] = useState<AddressPlus[]>([]);

  // ─── Effects ───────────────────────────────────────────────────────────────
  useEffect(() => {
    const successSwitchTranslation = t(
      "delivery:successfly-switched-franchise"
    );
    const switchBranchAddress = localStorage.getItem(
      LocalStorageEnum.SWITCH_ADDRESS
    );

    // Addresses always filter by the currently visible tab, not by the last
    // confirmed config — currentDeliveryConfig only updates on Apply, not on tab switch.
    const effectiveDeliveryTypeId = getDeliveryTypeIdForTab(selectedTab);

    // V3 maps every saved address and filters per-tab downstream. Legacy must
    // keep its original behavior: only show addresses matching the current
    // delivery config (or all when no config is set yet), otherwise addresses
    // of other delivery types leak into the list.
    const savedAddressesForList = hasWelcomeDrawerV3
      ? delivery.savedAddresses
      : delivery.savedAddresses.filter(
          (address: Address) =>
            address.deliveryTypeId ===
              currentDeliveryConfig?.deliveryType?.id || !currentDeliveryConfig
        );

    const newSavedAddresses = savedAddressesForList.map((address: Address) => {
      const available = checkAddressAvailabliltyInFranchisesV2(
        address,
        franchises,
        franchise,
        effectiveDeliveryTypeId
      );

      if (
        switchBranchAddress &&
        address.id?.toString() === switchBranchAddress &&
        available.zoneConfig
      ) {
        dispatch(setSelectedAddress(address));
        dispatch(
          setAnimatedGlobalBannerState({
            isOpen: true,
            text: successSwitchTranslation,
            warning: false,
          })
        );
        dispatch(setAddressSelectionDrawerState(false));
        localStorage.removeItem(LocalStorageEnum.SWITCH_ADDRESS);
      }

      return available;
    });

    newSavedAddresses.sort((a, b) => sortAddress(a, b));
    setAddresses(newSavedAddresses as AddressPlus[]);
    // franchise (router.query) can still be undefined on the very first
    // render during Next.js hydration — without it as a dependency, the
    // SWITCH_ADDRESS restore above only gets one (possibly premature) shot
    // and never retries once the query param is actually ready.
  }, [delivery.savedAddresses, currentDeliveryConfig, selectedTab, franchise]); // eslint-disable-line

  // Re-sync tab when drawer opens
  useEffect(() => {
    if (drawerState) {
      setSelectedTab(getInitialTab());
      setSearchQuery("");
      setSelectedFranchiseId(selectedFranchiseRedux?.id);
    }
  }, [drawerState]); // eslint-disable-line

  const selectedTabDeliveryTypeId = getDeliveryTypeIdForTab(selectedTab);

  // Only table-delivery renders the saved-address flow — every other delivery
  // type (car pickup, beach delivery, toktok pickup, ...) renders the
  // franchise list like takeout/dine-in. Checked by name, not usesMap — some
  // non-table delivery types are still flagged usesMap:true in config data.
  const isDeliveryTab =
    selectedTabDeliveryTypeId !== undefined &&
    activeFranchises
      .flatMap((f) => f.franchiseConfig?.deliveryV2 ?? [])
      .find((c) => c.deliveryType.id === selectedTabDeliveryTypeId)
      ?.deliveryType.displayName === DeliveryTypeDisplayName.TABLE_DELIVERY;

  // Franchises filtered to selected tab
  // Branches that offer the selected tab's order type, before the search box
  // narrows them. Kept separate so typing in search can't invalidate an
  // already-picked branch.
  const tabFranchises = useMemo(() => {
    if (selectedTabDeliveryTypeId !== undefined) {
      return activeFranchises.filter((f) =>
        f.franchiseConfig?.deliveryV2?.some(
          (d) => d.deliveryType.id === selectedTabDeliveryTypeId
        )
      );
    }
    if (selectedTab === OrderTypesEnum.TAKE_OUT) {
      return activeFranchises.filter((f) => f.franchiseConfig?.takeOut);
    }
    if (selectedTab === OrderTypesEnum.DINE_IN) {
      return activeFranchises.filter((f) => f.franchiseConfig?.dineIn);
    }

    return activeFranchises;
  }, [activeFranchises, selectedTab]);

  const filteredFranchises = useMemo(() => {
    if (!searchQuery) {
      return tabFranchises;
    }

    const q = searchQuery.toLowerCase();

    return tabFranchises.filter(
      (f) =>
        (f.address || f.name || "").toLowerCase().includes(q) ||
        (f.addressLocalized || "").toLowerCase().includes(q)
    );
  }, [tabFranchises, searchQuery]);

  // selectedFranchiseId survives a tab switch, but the branch it points at may
  // not offer the newly selected order type — and then it isn't in the list, so
  // nothing looks selected and there is nothing to un-pick. Applying in that
  // state used to resolve the id against the UNFILTERED activeFranchises and
  // commit e.g. "dine-in" at a pickup-only branch (prepits/project#3913).
  // Resolving against the tab's own branches makes the stale id simply not
  // resolve, which also keeps Apply disabled until a valid branch is picked.
  const selectedFranchiseForTab = tabFranchises.find(
    (f) => f.id === selectedFranchiseId
  );

  // Handler: apply for non-delivery tab
  const handleApplyFranchise = () => {
    if (!selectedFranchiseId) {
      return;
    }

    const targetFranchise = selectedFranchiseForTab;

    if (!targetFranchise) {
      return;
    }

    reportOrderTypeChangeIfNeeded();

    const orderType =
      selectedTabDeliveryTypeId !== undefined
        ? OrderTypesEnum.DELIVERYV2
        : (selectedTab as unknown as OrderTypesEnum);

    const matchedDeliveryConfig =
      selectedTabDeliveryTypeId !== undefined
        ? (targetFranchise.franchiseConfig?.deliveryV2 ?? []).find(
            (c) => c.deliveryType.id === selectedTabDeliveryTypeId
          )
        : undefined;

    // Switching to a different franchise with items already in the cart needs
    // confirmation first — same guard as the legacy SelectOrderInfo proceed button.
    if (
      cartItemsNumber > 0 &&
      selectedFranchiseRedux &&
      selectedFranchiseRedux.id !== targetFranchise.id
    ) {
      dispatch(setAddressSelectionDrawerState(false));
      handleModal(true, "SwitchFranchise/SwitchFranchise", {
        componentProps: {
          franchise: targetFranchise,
          returnPage: PagesEnum.Home,
          orderType,
          // Commit the picked delivery type through the switch so the landing
          // page doesn't keep a stale config (prepits/project#3913).
          deliveryConfig: matchedDeliveryConfig ?? null,
        },
      });

      return;
    }

    dispatch(setSelectedFranchise(targetFranchise));
    dispatch(setQueryParam(orderType));
    dispatch(setCurrentDeliveryConfig(matchedDeliveryConfig ?? null));
    dispatch(setAddressSelectionDrawerState(false));

    // Prevents useFranchiseData from force-reopening the welcome drawer on
    // a later Home mount — it treats a missing cookie as "no franchise ever
    // confirmed".
    const chooseDate = new Date();
    chooseDate.setHours(chooseDate.getHours() + 2);
    Cookies.set(
      COOKIE_ENUM.LAST_SELECTED_FRANCHISE,
      // slug can be null on the slug-less main franchise; fall back to id.
      String(targetFranchise.slug ?? targetFranchise.id),
      { expires: chooseDate }
    );

    const link = getPageLink(router, getDrawerReturnPage(), {
      franchiseSlug: targetFranchise.slug,
      orderType,
    });
    router.push(link.href, link.as);
  };

  // ─── Handlers ──────────────────────────────────────────────────────────────
  const handleSelectAddress = (address: AddressPlus) => {
    if (address.isInCurrentFranchise) {
      if (address.zoneConfig) {
        dispatch(setSelectedAddress(address));
        dispatch(setAddressSelectionDrawerState(false));
      }
    } else {
      const availableFranchises = address.availableDeliveryFranchises?.filter(
        (f) => !f.franchiseConfig.catering
      );

      if (availableFranchises && availableFranchises.length > 0) {
        dispatch(setAddressSelectionDrawerState(false));
        handleModal(true, "SwitchFranchise/SwitchFranchise", {
          componentProps: {
            franchise: availableFranchises[0],
            returnPage: PagesEnum.Home,
            deliveryAddressId: address.id,
            orderType: isDeliveryTab ? OrderTypesEnum.DELIVERYV2 : undefined,
          },
        });
      }
    }
  };

  // Apply from delivery tab: set address + orderType, navigate to home
  const handleApplyDelivery = (address: AddressPlus) => {
    if (address.isInCurrentFranchise) {
      if (!address.zoneConfig) {
        return;
      }

      reportOrderTypeChangeIfNeeded();

      // Update currentDeliveryConfig to match the active tab on Apply —
      // tab switch only updates local state, commit happens here.
      // The address is in the SELECTED branch (isInCurrentFranchise), so the
      // row committed must be that branch's own row for the tab's type. The
      // first row of the type across all branches belongs to whichever branch
      // is listed first, and the location screen then validated this very
      // address against that other branch's zones -- see
      // findDeliveryConfigForBranch.
      const applyDeliveryTypeId = getDeliveryTypeIdForTab(selectedTab);
      if (applyDeliveryTypeId !== undefined) {
        const matchingConfig = findDeliveryConfigForBranch(
          selectedFranchiseRedux,
          activeFranchises,
          (c) => c.deliveryType.id === applyDeliveryTypeId
        );
        if (matchingConfig) {
          dispatch(setCurrentDeliveryConfig(matchingConfig));
        }
      }

      // Stamp the applied delivery type onto the address so CartScreen's
      // deliveryTypeId !== currentDeliveryConfig.deliveryType.id check doesn't
      // re-open the drawer after navigating back to cart.
      dispatch(
        setSelectedAddress(
          applyDeliveryTypeId !== undefined
            ? { ...address, deliveryTypeId: applyDeliveryTypeId }
            : address
        )
      );
      dispatch(setQueryParam(OrderTypesEnum.DELIVERYV2));
      dispatch(setAddressSelectionDrawerState(false));
      const link = getPageLink(router, getDrawerReturnPage(), {
        franchiseSlug: selectedFranchiseRedux?.slug,
        orderType: OrderTypesEnum.DELIVERYV2,
      });
      router.push(link.href, link.as);
    } else {
      // Cross-franchise: fall back to switch-franchise modal flow
      handleSelectAddress(address);
    }
  };

  const handleDeleteAddress = (address: AddressPlus) => {
    handleModal(true, "DeleteAddressConfirmation/DeleteAddressConfirmation", {
      componentProps: { deliveryAddress: address },
    });
    dispatch(setAddressSelectionDrawerState(false));
  };

  const handleCloseDrawer = () => {
    dispatch(setAddressSelectionDrawerState(false));
  };

  const handleAddNewLocation = () => {
    // Carry the tab's own order type. Without it getPageLink keeps whatever
    // order_type is already in the URL — e.g. opening this drawer from a
    // takeout cart leaves `order_type=takeout-on-table`, so /location opened on
    // the Pick up branch list instead of the delivery address flow the customer
    // just asked for (prepits/project#3913 UX case 4).
    // Use the canonical DELIVERYV2, never the raw tab id: `order_type` is
    // carried forward by every later getPageLink, so a `delivery_type_<id>`
    // value leaks down the whole flow (the cart URL then never becomes
    // `deliveryv2`). Commit the tab's config first so the location screen's
    // resolveDeliveryV2Tab lands on the right delivery tab — same order as
    // SelectOrderInfoV3.handleProceed.
    // Read the currently committed delivery type BEFORE overwriting it below —
    // for a deliveryV2 order type (car pickup, cabin, ...) this is the only
    // record of the specific type, since the coarse order_type is just
    // "deliveryv2" and we're about to replace currentDeliveryConfig.
    const prevDeliveryTypeId = currentDeliveryConfig?.deliveryType?.id;

    // The selected branch's own row for the tab's type, not the first
    // branch's -- see findDeliveryConfigForBranch.
    const tabDeliveryConfig =
      selectedTabDeliveryTypeId !== undefined
        ? findDeliveryConfigForBranch(
            selectedFranchiseRedux,
            activeFranchises,
            (c) => c.deliveryType.id === selectedTabDeliveryTypeId
          )
        : undefined;

    if (tabDeliveryConfig) {
      dispatch(setCurrentDeliveryConfig(tabDeliveryConfig));
    }

    // Stamp the order type the customer currently has so backing out of the
    // location screen can restore it. The URL will read order_type=deliveryv2
    // for the address flow and the location screen syncs that into redux, so
    // this is the only surviving record of the pre-flow order type (e.g.
    // takeout, or car pickup via its delivery type id) — see v3HandleBack
    // (prepits/project#3913).
    const restoreParams: string[] = [];
    if (nextPage && orderTypeParam) {
      restoreParams.push(`${URL_PARAMS.PREV_ORDER_TYPE}=${orderTypeParam}`);
    }
    if (nextPage && prevDeliveryTypeId !== undefined) {
      restoreParams.push(
        `${URL_PARAMS.PREV_DELIVERY_TYPE_ID}=${prevDeliveryTypeId}`
      );
    }

    router.push(
      `${
        getPageLink(router, PagesEnum.Location, {
          nextPage,
          orderType: tabDeliveryConfig ? OrderTypesEnum.DELIVERYV2 : undefined,
          queryParams: restoreParams.length ? restoreParams : undefined,
        }).as
      }`
    );
  };

  return {
    // Redux-derived
    orderTypeParam,
    drawerState,
    delivery,
    isDeliveryDrawerInverted,
    // State
    addresses,
    // Franchises & tabs
    uniqueDeliveryTypes,
    hasTakeOut,
    hasDineIn,
    selectedTab,
    setSelectedTab,
    searchQuery,
    setSearchQuery,
    handleBranchSearchChange,
    selectedFranchiseId,
    setSelectedFranchiseId,
    selectedFranchiseForTab,
    filteredFranchises,
    isDeliveryTab,
    handleApplyFranchise,
    // Handlers
    handleSelectAddress,
    handleApplyDelivery,
    handleDeleteAddress,
    handleCloseDrawer,
    handleAddNewLocation,
    // Translations
    selectAddressTranslation: t("delivery:select-delivery-address"),
    outsideDeliveryTranslation: t("delivery:outside-restaurant-delivery"),
    addNewLocationTranslation: t("delivery:add-new-location"),
    t,
  };
};
